/**
 * Gameplay data: countries, cities and curated quest locations.
 * Generated by scripts/build-data.mjs from the upstream Natural Earth data.
 */

export interface QuestCountry {
  alpha3: string;
  alpha2: string;
  name: string;
  nameLong: string;
  nameSort: string;
  nameOfficial: string;
  abbreviation: string;
  continent: string;
}

export interface QuestCity {
  name: string;
  displayName: string;
  lat: number;
  lon: number;
  population: number;
  isCapital: boolean;
}

export interface QuestLocation {
  country: QuestCountry;
  city: QuestCity;
  overrideCountryName: string | null;
  overrideFlagCode: string | null;
  weight: number;
}

export interface QuestSettings {
  cityWeight: number;
  populationWeight: number;
  minorCityPopulationThreshold: number;
  minorCityWeight: number;
}

export interface QuestData {
  settings: QuestSettings;
  locations: QuestLocation[];
}

export async function loadQuestData(url = 'assets/data/quest.json'): Promise<QuestData> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to load quest data: ${res.status}`);
  return (await res.json()) as QuestData;
}

/**
 * Port of Country.GetPreferredDisplayName: pick the clearest of the available names,
 * preferring short, unabbreviated ones.
 */
export function countryDisplayName(country: QuestCountry, maxDesiredLength = Infinity): string {
  const rankedNames = [
    country.name,
    country.nameLong,
    country.nameOfficial,
    country.nameSort,
    country.abbreviation,
  ];

  let best = rankedNames[0];
  let bestPenalty = Infinity;
  for (let i = 0; i < rankedNames.length; i++) {
    const current = rankedNames[i];
    if (!current || current.length === 0) continue;
    let penalty = i;
    penalty += current.length;
    if (current.length > maxDesiredLength) penalty += 1000;
    if (i === 4 || current.includes('.')) {
      penalty += 100;
      penalty -= current.length * 2;
    }
    if (penalty < bestPenalty) {
      bestPenalty = penalty;
      best = current;
    }
  }
  return best;
}

export function locationCountryName(location: QuestLocation, maxDesiredLength = Infinity): string {
  return location.overrideCountryName ?? countryDisplayName(location.country, maxDesiredLength);
}
